import React from 'react';
import { PropertyOperation } from 'src/api/generated';
import { ActivitySection } from 'src/modules/shared/components/molecules/activity-section';
import { EventTimeline } from 'src/modules/shared/components/molecules/event-timeline';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import type { I18nContextValue } from 'src/modules/shared/types/i18n/i18n.type';
import {
  buildIntegrationActivityEventTags,
  getIntegrationActivityEventLabelKey,
  resolveIntegrationActivityEventTone,
} from 'src/modules/shared/utils/integrations/integration-activity-event.util';
import {
  formatDate,
  formatTimeDistance,
} from 'src/modules/shared/utils/formatting/date.util';
import type { PropertySyncTimelineItem } from '../../types/property-sync-events.type';

interface PropertySyncTimelineProps {
  items: PropertySyncTimelineItem[];
  loading: boolean;
}

export const PropertySyncTimeline: React.FC<PropertySyncTimelineProps> = ({
  items,
  loading,
}) => {
  const { language, t } = useI18n();

  return (
    <ActivitySection loading={loading} contentClassName="max-h-56">
      <EventTimeline
        items={items.map((item, index) => ({
          key: `${item.publicationId ?? 'property'}-${item.event.id}-${index}`,
          label: buildTimelineLabel({ item, t }),
          value: formatTimeDistance(new Date(item.createdAt), true, language),
          valueTooltip: formatDate(item.createdAt),
          tone: resolveIntegrationActivityEventTone(item.event),
          tags: buildIntegrationActivityEventTags(item.event, t),
        }))}
        emptyLabel={t(
          'catalog.properties.editor.publishing.syncTimeline.empty'
        )}
      />
    </ActivitySection>
  );
};

const getPublicationLabel = ({
  item,
  t,
}: {
  item: PropertySyncTimelineItem;
  t: I18nContextValue['t'];
}) => {
  switch (item.operation) {
    case PropertyOperation.SALE:
      return t('catalog.properties.operation.SALE');
    case PropertyOperation.RENT:
      return t('catalog.properties.operation.RENT');
    case PropertyOperation.TEMPORARY_RENT:
      return t('catalog.properties.operation.TEMPORARY_RENT');
    case PropertyOperation.SALE_AND_RENT:
      return t('catalog.properties.operation.SALE_AND_RENT');
    case null:
      return (
        item.externalSourceId ??
        t('catalog.properties.editor.publishing.publicationGeneric')
      );
  }
};

const buildTimelineLabel = ({
  item,
  t,
}: {
  item: PropertySyncTimelineItem;
  t: I18nContextValue['t'];
}) => {
  const eventLabel = t(getIntegrationActivityEventLabelKey(item.event.type));
  const publicationLabel = getPublicationLabel({ item, t });

  return publicationLabel ? `${publicationLabel} · ${eventLabel}` : eventLabel;
};
